import type { FC, MouseEvent } from 'react'
import { useState } from 'react'

import { Input, StyledText } from '@skytech/ui'
import { Button } from '@skytech/ui/Button'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import ArrowBackIcon from 'mdi-react/ArrowBackIcon'
import RemoveIcon from 'mdi-react/RemoveIcon'

import { Translation } from '@/app/translations/Translation'
import Highlight from '@/components/Highlight'

import type { NumberRecordType } from '../types'

import queries from '../../queries'
import adminResources from '../../resources'

type Props = {
  scrollToNextSlide: (index: number) => void
  scrollToNextPage: (index: number) => void
  openGroupModal: () => void
  onSelectNumberHandler: (numbers: Map<string, NumberRecordType>) => void
  selectedNumbers: Map<string, NumberRecordType>
}

export const SelectedPhoneNumbers: FC<Props> = ({
  scrollToNextPage,
  scrollToNextSlide,
  selectedNumbers,
  onSelectNumberHandler,
  openGroupModal,
}) => {
  const [searchPhrase, setSearchPhrase] = useState('')
  const queryClient = useQueryClient()

  const anySelected = () => selectedNumbers.size > 0

  const addNumbersToPhoneBook = useMutation(adminResources.addNumbersToPhoneBook, {
    onSuccess() {
      queryClient.invalidateQueries(queries.adminActiveNumbersList())
    },
  })

  const onSendHandler = (e: MouseEvent) => {
    e.preventDefault()
    if (anySelected()) {
      scrollToNextPage(1)
    }
  }

  const onClickNumber = (fullNumber: string, item: NumberRecordType) => {
    const map = new Map()
    map.set(fullNumber, item)
    onSelectNumberHandler(map)
  }

  const onAddToPhoneBook = () => {
    if (anySelected()) {
      // We spread the map here because datatype Map does not have a .map function like Arrays
      const numbers = [...selectedNumbers].map(([, item]) => {
        const { name, countryId, number } = item
        return { name, countryId, number }
      })
      addNumbersToPhoneBook.mutate({ numbers })
    }
  }

  return (
    <div className="flex w-full flex-shrink-0 snap-start flex-col md:px-5 lg:w-1/3">
      {/* //SearchBar */}
      <div className="flex w-full flex-col">
        <div className="flex items-center">
          <ArrowBackIcon
            onClick={() => scrollToNextSlide(1)}
            className="mb-4 mr-3 block lg:hidden"
          />
          <h3 className="lg:mb-2 lg:leading-10">
            <Translation id="Selected: " />
            {selectedNumbers.size}
          </h3>
        </div>
        <div className="grow-0">
          <Input
            isFullWidth
            id="search"
            marginBottom={16}
            onChange={e => setSearchPhrase(e.target.value?.toLowerCase() || '')}
          />
        </div>
      </div>
      {/* //Body */}
      <div className="w-full grow overflow-y-auto">
        {/* We spread the map here because datatype Map does not have a .map function like Arrays */}
        {[...selectedNumbers].map(([fullNumber, item]) => {
          const { name, countryId, number } = item
          let numberL = number.toLowerCase()
          const nameL = name?.toLowerCase() || ''
          const countryString = countryId?.toString()?.toLowerCase() || ''

          if (
            !numberL.includes(searchPhrase) &&
            !nameL.includes(searchPhrase) &&
            !countryString.includes(searchPhrase)
          )
            return null
          return (
            <div
              key={fullNumber}
              className="flex w-full cursor-pointer justify-between rounded p-2 hover:bg-gray-50"
              onClick={() => onClickNumber(fullNumber, item)}
              role="button"
              tabIndex={0}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  onClickNumber(fullNumber, item)
                }
              }}
            >
              <div
                onClick={() => onClickNumber(fullNumber, item)}
                role="button"
                tabIndex={0}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    onClickNumber(fullNumber, item)
                  }
                }}
                className={`grow-1 ${!countryId ? 'text-red-600' : ''}`}
              >
                <div>
                  {countryString && (
                    <StyledText size="regular-plus">
                      <Highlight pattern={searchPhrase} text={`+${countryString}`} />{' '}
                    </StyledText>
                  )}
                  <Highlight pattern={searchPhrase} text={numberL} />
                </div>
                <span className="text-sm text-gray-400">
                  <Highlight pattern={searchPhrase} text={name} />
                </span>
              </div>
              <RemoveIcon
                onClick={() => onClickNumber(fullNumber, item)}
                role="button"
                tabIndex={0}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    onClickNumber(fullNumber, item)
                  }
                }}
                size={20}
              />
            </div>
          )
        })}
      </div>
      <div className="flex flex-col md:flex-row md:gap-3">
        <Button
          className="mx-0 md:w-1/2"
          onClick={openGroupModal}
          disabled={!anySelected()}
          isLoading={addNumbersToPhoneBook.isLoading}
        >
          <Translation id="Add to group" />
        </Button>
        <Button
          className="mx-0 md:w-1/2"
          onClick={onAddToPhoneBook}
          disabled={!anySelected()}
          isLoading={addNumbersToPhoneBook.isLoading}
        >
          <Translation id="Add to phone book" />
        </Button>
      </div>
      <div>
        <Button
          variant="primary"
          className="mx-0 w-full"
          onClick={onSendHandler}
          disabled={!anySelected()}
          isLoading={addNumbersToPhoneBook.isLoading}
        >
          <Translation id="Send SMS to selected" />
        </Button>
      </div>
    </div>
  )
}
